import { createFileRoute, Link } from "@tanstack/react-router";
import { cta } from "@/lib/analytics";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { RelatedLinks } from "@/components/related-links";
import { BookingCta } from "@/components/booking-cta";
import { ONE_PRICE_FALLBACK, ONE_RESERVATION_FLOW, TODO, sessionDetails } from "@/content/booking";
import { FaqDetails } from "@/components/faq-details";
import portrait from "@/assets/sylwia-portrait.jpg.asset.json";
import ogSocial from "@/assets/og-sylwia-sitkowska.jpg";
import { abs, serviceSchema, breadcrumbSchema, personSchema, webPageSchema } from "@/content/seo";

const title = "Konsultacje dla liderów i przedsiębiorców | Sylwia Sitkowska";
const description =
  "Konsultacje 1:1 dla liderów, którzy potrzebują poufnej rozmowy o decyzjach, ludziach i presji. Online lub Warszawa, 90 minut, 1 000 zł brutto.";
const ogImage = abs(ogSocial);

const faq = [
  {
    q: "Jak wygląda konsultacja psychologiczna dla lidera?",
    a: "Spotkanie trwa 90 minut i dotyczy konkretnej sytuacji: decyzji, konfliktu, przeciążenia albo zmiany w firmie. Rozmawiamy o tym, co dzieje się w organizacji, i o tym, co ta sytuacja robi z Tobą. Na koniec zwykle porządkujemy możliwe kierunki i ustalamy, czy potrzebna jest dalsza praca.",
  },
  {
    q: "Czy pierwsze spotkanie zobowiązuje do dalszej pracy?",
    a: "Nie. Pierwsze spotkanie jest pełnoprawną, płatną sesją i nie zobowiązuje Cię do kontynuowania współpracy. Decyzję o dalszych krokach podejmujesz samodzielnie.",
  },
  {
    q: "Czym konsultacja różni się od psychoterapii?",
    a: "Konsultacja to jedno lub kilka spotkań poświęconych konkretnej sytuacji: decyzji, konfliktowi, zmianie. Psychoterapia to dłuższy proces prowadzony po wspólnym ustaleniu celu i kontraktu.",
  },
  {
    q: "Czy mogę umówić tylko jedno spotkanie?",
    a: "Tak. Jedno spotkanie bywa wystarczające, żeby uporządkować sytuację albo otrzymać rekomendację dalszej ścieżki.",
  },
  {
    q: "Czy spotkania są poufne?",
    a: "Tak. Obowiązuje pełna poufność i dyskrecja, zarówno co do treści rozmów, jak i samego faktu współpracy.",
  },
  {
    q: "Czy spotkania odbywają się online?",
    a: "Tak. Spotkania mogą odbywać się online lub osobiście, formę wybierasz przy rezerwacji terminu.",
  },
  {
    q: "Jak wygląda płatność?",
    a: "Wybierasz termin, potwierdzam go i wysyłam dane do płatności. Koszt to 1 000 zł brutto (usługa zwolniona z VAT).",
  },
  {
    q: "Czy otrzymam fakturę?",
    a: "Tak. Fakturę wystawiam do każdego spotkania. Wystarczy podać dane do faktury.",
  },
  {
    q: "Ile trwa spotkanie i gdzie się odbywa?",
    a: "Spotkanie trwa 90 minut. Odbywa się online lub stacjonarnie w Warszawie, pl. Konstytucji 6/74.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faq.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: { "@type": "Answer", text: item.a },
  })),
};


const serviceJsonLd = serviceSchema({
  name: "Konsultacje indywidualne",
  description,
  path: "/konsultacje",
  price: "1000",
  audience: "Właściciele firm, członkowie zarządów i liderzy najwyższego szczebla",
  serviceType: "Indywidualna praca psychologiczna dla liderów",
});

const breadcrumbJsonLd = breadcrumbSchema([
  { name: "Strona główna", path: "/" },
  { name: "Konsultacje indywidualne", path: "/konsultacje" },
]);

const pageJsonLd = webPageSchema({
  title,
  description,
  path: "/konsultacje",
  aboutName: "Konsultacje indywidualne dla liderów",
  speakableSelectors: ["h1"],
});

export const Route = createFileRoute("/konsultacje")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      {
        name: "keywords",
        content:
          "konsultacja psychologiczna, konsultacje psychologiczne online, psychologia biznesu, psycholog biznesowy Warszawa, psycholog dla przedsiębiorców, konsultacje indywidualne dla liderów, coaching menedżerski, wsparcie psychologiczne dla właścicieli firm, Sylwia Sitkowska konsultacje",
      },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://sylwiasitkowska.pl/konsultacje" },
      { property: "og:image", content: ogImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: ogImage },
    ],
    links: [{ rel: "canonical", href: "https://sylwiasitkowska.pl/konsultacje" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(serviceJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(pageJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(personSchema) },
    ],
  }),
  component: MMOnePage,
});

const heroFacts = [
  "Indywidualnie",
  "Online lub Warszawa, pl. Konstytucji 6/74",
  "90 minut",
  "1 000 zł brutto (zwolnione z VAT)",
  "Dalszą formę pracy ustalamy podczas pierwszego spotkania",
];

const dlaKogo = [
  "Stoisz przed trudną decyzją, którą trzeba podjąć przy niepełnych informacjach?",
  "Mierzysz się z konfliktem albo narastającym napięciem w zespole?",
  "Trudno Ci delegować i odpuszczać, wiele spraw wraca ostatecznie do Ciebie?",
  "Czujesz ciężar odpowiedzialności za ludzi, firmę i decyzje, których konsekwencje ponoszą także inni?",
  "Brakuje Ci bezpiecznego miejsca, w którym możesz mówić otwarcie o tym, co się dzieje w Tobie?",
  "Widzisz, że sposób, w jaki pracujesz, zaczyna odbijać się na zdrowiu, relacjach albo życiu poza firmą?",
];

const formy = [
  {
    n: "01",
    title: "Konsultacja dotycząca konkretnej sytuacji",
    desc: "Jedno lub kilka spotkań poświęconych decyzji, konfliktowi, zmianie lub innemu aktualnemu wyzwaniu.",
  },
  {
    n: "02",
    title: "Cykl pracy indywidualnej",
    desc: "Regularna praca nad sposobem funkcjonowania w roli lidera, podejmowania decyzji, budowania granic i reagowania pod presją.",
  },
  {
    n: "03",
    title: "Praca psychoterapeutyczna",
    desc: "W uzasadnionych przypadkach, po wspólnym ustaleniu celu i kontraktu, współpraca może przyjąć formę psychoterapii.",
  },
];

const kroki = [
  ONE_RESERVATION_FLOW,
  "Podczas spotkania omawiamy aktualną sytuację i oczekiwany cel.",
  "Pod koniec ustalamy, czy potrzebna jest pojedyncza konsultacja, cykl pracy, psychoterapia czy inna forma pracy.",
];

function SectionMark({ number, label }: { number?: string; label: string }) {
  return (
    <div className="flex items-baseline gap-4">
      {number && <span className="font-display text-sm text-ink-soft">{number}</span>}
      <p className="eyebrow">{label}</p>
    </div>
  );
}

function MMOnePage() {
  const practical = sessionDetails.map((item) => ({
    label: item.label,
    value: item.value ?? (item.label === "Cena" ? ONE_PRICE_FALLBACK : TODO),
    pending: !item.value,
  }));


  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="tresc-glowna">
        {/* Hero */}
        <section className="border-b border-steel">
          <div className="container-page section-pad">
            <nav aria-label="Okruszki" className="mb-8 text-sm text-ink-soft">
              <ol className="flex flex-wrap items-center gap-2">
                <li>
                  <Link to="/" className="underline underline-offset-4 hover:text-coral-text">
                    Strona główna
                  </Link>
                </li>
                <li aria-hidden="true">/</li>
                <li aria-current="page" className="text-ink">
                  Konsultacje indywidualne
                </li>
              </ol>
            </nav>
            <SectionMark label="Konsultacje indywidualne" />
            <h1 className="serif-display mt-8 max-w-3xl text-[2.4rem] md:text-[3.6rem]">
              Indywidualna praca dla ludzi, od których wiele zależy
            </h1>
            <div className="mt-10 h-px w-full bg-steel" />
            <p className="measure mt-8 text-base leading-relaxed text-ink/80 md:text-lg">
              Indywidualne spotkania, podczas których możemy pracować nad konkretną decyzją,
              kryzysem, relacją albo nad powtarzającym się sposobem, w jaki reagujesz i
              funkcjonujesz pod presją.
            </p>

            <ul className="stagger mt-10 grid gap-px border border-steel bg-steel sm:grid-cols-2">
              {heroFacts.map((fact) => (
                <li key={fact} className="bg-background p-5 text-base leading-relaxed text-ink/80">
                  {fact}
                </li>
              ))}
            </ul>

            <div className="mt-10">
              <BookingCta cta={cta("konsultacje-rezerwacja", "hero", "konsultacja")}>Zarezerwuj pierwsze spotkanie</BookingCta>
            </div>
          </div>
        </section>

        {/* Dla kogo */}
        <section className="border-b border-steel bg-surface">
          <div className="container-page grid gap-10 py-12 md:grid-cols-[0.4fr_1fr] md:gap-16 md:py-16">
            <div>
              <SectionMark number="01" label="Dla kogo" />
              <h2 className="display-xl mt-8 text-[1.9rem] md:text-[2.4rem]">
                Dla właścicieli firm i osób, które odpowiadają za innych
              </h2>
            </div>
            <div className="max-w-2xl">
              <div className="h-px w-full bg-steel" />
              <p className="mt-8 text-base leading-relaxed text-ink/80 md:text-lg">
                Pracuję z osobami, które odpowiadają za ludzi, pieniądze i kierunek organizacji.
                Często mają wokół siebie wiele osób, ale niewiele takich, z którymi mogą naprawdę
                otwarcie porozmawiać o własnych wątpliwościach, błędach i trudnościach.
              </p>
              <ul className="mt-8 space-y-4">
                {dlaKogo.map((item) => (
                  <li key={item} className="flex gap-4 text-base leading-relaxed text-ink/80">
                    <span aria-hidden="true" className="mt-2 size-1.5 shrink-0 bg-coral" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Formy pracy */}
        <section className="border-b border-steel">
          <div className="container-page section-pad">
            <SectionMark number="02" label="Jaką formę może przyjąć praca" />
            <div className="mt-10 grid gap-px border border-steel bg-steel md:grid-cols-3">
              {formy.map((item) => (
                <article key={item.n} className="min-w-0 bg-background p-8">
                  <span className="font-display text-sm text-ink-soft">{item.n}</span>
                  <h3 className="display-xl mt-4 hyphens-auto break-words text-[1.3rem] md:text-[1.5rem]" lang="pl">{item.title}</h3>
                  <p className="mt-4 text-base leading-relaxed text-ink/80">{item.desc}</p>
                </article>
              ))}
            </div>
            <p className="measure mt-8 text-base leading-relaxed text-ink/80">
              Odpowiednią formę ustalamy wspólnie podczas pierwszego spotkania. Jeśli inny rodzaj
              pomocy będzie bardziej właściwy, otrzymasz jasną rekomendację dalszej ścieżki.
            </p>
          </div>
        </section>

        {/* Pierwsze spotkanie */}
        <section className="border-b border-steel bg-surface">
          <div className="container-page grid gap-10 py-12 md:grid-cols-[0.4fr_1fr] md:gap-16 md:py-16">
            <div>
              <SectionMark number="03" label="Jak wygląda pierwsze spotkanie" />
            </div>
            <div className="max-w-2xl">
              <ol className="border-t border-steel">
                {kroki.map((krok, i) => (
                  <li
                    key={krok}
                    className="grid gap-2 border-b border-steel py-5 md:grid-cols-[3rem_1fr]"
                  >
                    <span className="font-display text-sm text-ink-soft">{`0${i + 1}`}</span>
                    <span className="text-base leading-relaxed text-ink/80">{krok}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        {/* Informacje praktyczne */}
        <section className="border-b border-steel">
          <div className="container-page section-pad">
            <SectionMark number="04" label="Informacje praktyczne" />
            <dl className="mt-10 border-t border-steel">
              {practical.map((item) => (
                <div
                  key={item.label}
                  className="grid gap-2 border-b border-steel py-5 md:grid-cols-[0.3fr_1fr]"
                >
                  <dt className="text-xs uppercase tracking-[0.16em] text-ink-soft">
                    {item.label}
                  </dt>
                  <dd
                    className={`text-base leading-relaxed ${item.pending ? "text-coral-text" : "text-ink/80"}`}
                  >
                    {item.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        {/* Prowadząca */}
        <section className="border-b border-steel bg-surface">
          <div className="container-page grid gap-10 py-12 md:grid-cols-[0.4fr_1fr] md:gap-16 md:py-16">
            <div>
              <SectionMark number="05" label="Prowadząca i kwalifikacje" />
            </div>
            <div className="max-w-2xl">
              <h2 className="display-xl text-[1.6rem] md:text-[2rem]">
                Prowadząca: Sylwia Sitkowska
              </h2>
              <p className="mt-6 text-base leading-relaxed text-ink/80 md:text-lg">
                Jestem psychologiem i psychoterapeutką, ale od kilkunastu lat sama prowadzę firmy.
                Nie musisz więc tłumaczyć mi, dlaczego czasem nie da się po prostu „odpuścić”,
                „postawić granicy” albo „mniej pracować”.
              </p>

              <Link
                to="/o-mnie"
                className="mt-6 inline-flex min-h-11 items-center text-base font-medium text-ink underline underline-offset-4 transition-colors hover:text-coral-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral"
              >
                Poznaj moje doświadczenie i kwalifikacje
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section aria-labelledby="faq-one" className="border-b border-steel">
          <div className="container-page section-pad">
            <SectionMark number="06" label="Najczęstsze pytania" />
            <h2 id="faq-one" className="display-xl mt-8 text-[1.9rem] md:text-[2.4rem]">
              Zanim zarezerwujesz
            </h2>
            <FaqDetails items={faq} className="mt-10" />
          </div>
        </section>

        {/* Końcowe CTA */}
        <section className="grain bg-ink">
          <div className="container-page py-12 text-mist md:py-16">
            <p className="eyebrow-light">Kolejny krok</p>
            <h2 className="display-xl mt-8 max-w-3xl text-[1.9rem] md:text-[2.6rem]">
              Jeśli chcesz sprawdzić, czy taka forma pracy jest dla Ciebie, zacznijmy od jednego
              spotkania.
            </h2>
            <div className="mt-10">
              <BookingCta cta={cta("konsultacje-rezerwacja", "domkniecie", "konsultacja")} className="btn-base border border-mist text-mist hover:bg-mist hover:text-ink">
                Zarezerwuj pierwsze spotkanie
              </BookingCta>
              <p className="mt-4 text-base leading-relaxed text-mist/80">
                Pierwsze spotkanie nie zobowiązuje do kontynuowania współpracy.
              </p>
            </div>
          </div>
        </section>
      </main>
      <RelatedLinks
        exclude={["/konsultacje"]}
        only={["/mastermind-dla-przedsiebiorcow", "/syndrom-oszusta", "/perfekcjonizm", "/mastermind"]}
        heading={"Przeczytaj i wybierz dalszy krok"}
      />
      <SiteFooter />
    </div>
  );
}
